import { useEffect, useMemo, useState } from 'react';
import { useConfiguration } from '../context/configurationContext';
import { getLandingPageSettingsApi } from '../util/api';
import {
  buildSubcategoriesByCategory,
  mapCategoryOptions,
  mapLandingPageLocations,
  QUICK_FILTER_OPTIONS,
} from '../util/homepageSearchOptions';

/**
 * Shared homepage search options for header and hero search forms.
 *
 * @param {Object} [options]
 * @param {Array} [options.prefetchedLocations] - skip fetch when parent already loaded settings
 * @param {boolean} [options.skipLocationFetch]
 */
const useHomepageSearchOptions = ({
  prefetchedLocations = null,
  skipLocationFetch = false,
} = {}) => {
  const config = useConfiguration();
  const listingCategories = config?.categoryConfiguration?.categories || [];

  const [locations, setLocations] = useState(() =>
    prefetchedLocations ? mapLandingPageLocations(prefetchedLocations) : []
  );
  const [loading, setLoading] = useState(!skipLocationFetch && prefetchedLocations == null);
  const [error, setError] = useState(null);

  const categories = useMemo(() => mapCategoryOptions(listingCategories), [listingCategories]);

  const subcategoriesByCategory = useMemo(
    () => buildSubcategoriesByCategory(listingCategories),
    [listingCategories]
  );

  useEffect(() => {
    if (prefetchedLocations) {
      setLocations(mapLandingPageLocations(prefetchedLocations));
      setLoading(false);
      setError(null);
    }
  }, [prefetchedLocations]);

  useEffect(() => {
    if (skipLocationFetch || prefetchedLocations != null) {
      return undefined;
    }

    let cancelled = false;

    const loadLocations = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getLandingPageSettingsApi();
        if (cancelled) {
          return;
        }
        const rawLocations = response?.data?.locations || [];
        setLocations(mapLandingPageLocations(rawLocations));
      } catch (err) {
        if (!cancelled) {
          console.error('[useHomepageSearchOptions] Failed to load locations:', err);
          setError('Failed to load locations');
          setLocations([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadLocations();

    return () => {
      cancelled = true;
    };
  }, [skipLocationFetch, prefetchedLocations]);

  return {
    locations,
    categories,
    subcategoriesByCategory,
    quickFilters: QUICK_FILTER_OPTIONS,
    loading,
    error,
    listingCategories,
  };
};

export default useHomepageSearchOptions;
